import React, { useState, useEffect } from 'react';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { TbPlus, TbEdit, TbTrash, TbFileText } from 'react-icons/tb';
import { Auth } from 'aws-amplify';
import TextEdit from '../../../molecules/TextEdit';
import SelectTextField from '../../../molecules/SelectTextField';
import ConfirmationModal from '../../../templates/ConfirmationModal';

// ✅ URL GENÉRICA - Funciona para PF e PJ
const BASE_URL = `${process.env.REACT_APP_PERSON_API_URL}/person`;
const API_KEY = process.env.REACT_APP_API_KEY || '';

interface Document {
    id?: string;
    type: string;
    number: string;
    issuingAuthority?: string;
    expeditionDate?: string;
    expirationDate?: string;
    province?: string;
}

interface Props {
    personId: string;  // ✅ UUID genérico (Individual ou Legal Person)
    personType?: 'individual' | 'legal_person';  // ✅ Para tipos dinâmicos e IDs únicos
    documents?: Document[];
    onDocumentsChange?: (documents: Document[]) => void;
}

const PersonFormDocuments: React.FC<Props> = ({
    personId,
    personType = 'individual',
    documents: initialDocuments = [],
    onDocumentsChange = () => {}
}) => {
    const isModalOpen = useSignal<boolean>(false);
    const isDeleteModalOpen = useSignal<boolean>(false);
    const isLoading = useSignal<boolean>(false);
    const documents = useSignal<Document[]>([]);
    
    const [editingDocument, setEditingDocument] = useState<Document | null>(null);
    const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
    const [form, setForm] = useState<Document>({
        type: '',
        number: '',
        issuingAuthority: '',
        expeditionDate: '',
        expirationDate: '',
        province: ''
    });

    // ✅ Tipos de documento baseados no personType
    const getValidDocumentTypes = () => {
        if (personType === 'legal_person') {
            return [
                { value: 'CNPJ', label: 'CNPJ' },
                { value: 'INSCRICAO_ESTADUAL', label: 'Inscrição Estadual' },
                { value: 'CONTRATO_SOCIAL', label: 'Contrato Social' },
                { value: 'ALVARA_FUNCIONAMENTO', label: 'Alvará de Funcionamento' },
                { value: 'CERTIDAO_NEGATIVA_DEBITOS', label: 'Certidão Negativa de Débitos' },
                { value: 'COMPROVANTE_ENDERECO', label: 'Comprovante de Endereço' }
            ];
        } else {
            return [
                { value: 'RG', label: 'RG' },
                { value: 'CNH', label: 'CNH' },
                { value: 'PASSPORT', label: 'Passaporte' },
                { value: 'RNE', label: 'RNE' },
                { value: 'CTPS', label: 'Carteira de Trabalho' },
                { value: 'TITULO_ELEITOR', label: 'Título de Eleitor' }
            ];
        }
    };

    const documentTypes = getValidDocumentTypes();

    const getDocumentTypeLabel = (type: string) => {
        const typeObj = documentTypes.find(t => t.value === type);
        return typeObj ? typeObj.label : type;
    };

    useEffect(() => {
        if (initialDocuments && initialDocuments.length > 0) {
            documents.value = initialDocuments;
        }
        
        if (personId) {
            console.log('🔄 Carregando documentos para person:', personId);
            loadDocuments();
        }
    }, [personId]);

    const openModal = (document?: Document) => {
        if (document) {
            setEditingDocument(document);
            setForm(document);
        } else {
            setEditingDocument(null);
            setForm({
                type: documentTypes[0]?.value || '',
                number: '',
                issuingAuthority: '',
                expeditionDate: '',
                expirationDate: '',
                province: ''
            });
        }
        isModalOpen.value = true;
    };

    const closeModal = () => {
        isModalOpen.value = false;
        setEditingDocument(null);
        setForm({
            type: documentTypes[0]?.value || '',
            number: '',
            issuingAuthority: '',
            expeditionDate: '',
            expirationDate: '',
            province: ''
        });
    };

    const validateForm = () => {
        if (!form.type) {
            toast.error('Selecione o tipo de documento');
            return false;
        }

        if (!form.number.trim()) {
            toast.error('Digite o número do documento');
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        isLoading.value = true;
        try {
            const url = editingDocument 
                ? `${BASE_URL}/${personId}/documents/${editingDocument.id}`
                : `${BASE_URL}/${personId}/documents`;
            
            const method = editingDocument ? 'PUT' : 'POST';
            
            const session = await Auth.currentSession();
            const token = session.getAccessToken().getJwtToken();
            
            const payload = {
                ...form,
                issuing_authority: form.issuingAuthority,
                expedition_date: form.expeditionDate,
                expiration_date: form.expirationDate
            };
            delete payload.issuingAuthority;
            delete payload.expeditionDate;
            delete payload.expirationDate;
            delete payload.id;
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'x-api-key': API_KEY,
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success(editingDocument ? 'Documento atualizado!' : 'Documento cadastrado!');
                await loadDocuments();
                closeModal();
            } else {
                const error = await response.json();
                toast.error(error.error || 'Erro ao salvar documento');
            }
        } catch (error) {
            toast.error('Erro ao salvar documento');
            console.error('Error saving document:', error);
        } finally {
            isLoading.value = false;
        }
    };

    const handleDelete = async () => {
        if (!documentToDelete) return;

        isLoading.value = true;
        try {
            const session = await Auth.currentSession();
            const token = session.getAccessToken().getJwtToken();
            
            const response = await fetch(`${BASE_URL}/${personId}/documents/${documentToDelete}`, {
                method: 'DELETE',
                headers: {
                    'x-api-key': API_KEY,
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                toast.success('Documento excluído!');
                await loadDocuments();
            } else {
                toast.error('Erro ao excluir documento');
            }
        } catch (error) {
            toast.error('Erro ao excluir documento');
            console.error('Error deleting document:', error);
        } finally {
            isLoading.value = false;
            isDeleteModalOpen.value = false;
            setDocumentToDelete(null);
        }
    };

    const loadDocuments = async () => {
        if (!personId) {
            console.log('⚠️ personId está vazio, não carregando documentos');
            return;
        }
        
        console.log('🔍 Carregando documentos para personId:', personId);
        isLoading.value = true;
        
        try {
            const session = await Auth.currentSession();
            const token = session.getIdToken().getJwtToken();
            
            const response = await fetch(`${BASE_URL}/${personId}/documents`, {
                headers: {
                    'x-api-key': API_KEY,
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.ok) {
                const data = await response.json();
                documents.value = data.documents || data || [];
                console.log('✅ Documentos carregados:', documents.value.length);
            } else {
                const errorText = await response.text();
                console.error('❌ Erro na resposta:', response.status, response.statusText, errorText);
            }
        } catch (error) {
            console.error('❌ Error loading documents:', error);
            documents.value = [];
        } finally {
            isLoading.value = false;
        }
    };

    const confirmDelete = (documentId: string) => {
        setDocumentToDelete(documentId);
        isDeleteModalOpen.value = true;
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        try {
            return new Date(dateString).toLocaleDateString('pt-BR');
        } catch {
            return dateString;
        }
    };

    return (
        <div className="p-4">
            <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                    <TbFileText className="text-blue-600" />
                    Documentos
                </h3>
                <button
                    id={`btn-add-document-${personType}`}  // ✅ ID único por tipo
                    onClick={() => openModal()}
                    className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700"
                >
                    <TbPlus size={16} />
                    Novo Documento
                </button>
            </div>

            <div className="bg-white rounded-lg shadow">
                {isLoading.value ? (
                    <div className="p-8 text-center text-gray-500">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-sm">Carregando documentos...</p>
                    </div>
                ) : documents.value.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                        <TbFileText size={48} className="mx-auto mb-4 text-gray-300" />
                        <p>Nenhum documento cadastrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Tipo</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Número</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Órgão Emissor</th>
                                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Expedição</th>
                                    <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Ações</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {documents.value.map((document, index) => (
                                    <tr key={document.id || index} className="hover:bg-gray-50">
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <TbFileText className="text-blue-600" />
                                                <span className="font-medium">{getDocumentTypeLabel(document.type)}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 font-mono text-sm">{document.number}</td>
                                        <td className="px-4 py-3 text-sm">{document.issuingAuthority || '-'}</td>
                                        <td className="px-4 py-3 text-sm">{formatDate(document.expeditionDate || '')}</td>
                                        <td className="px-4 py-3 text-center">
                                            <div className="flex justify-center gap-2">
                                                <button
                                                    id={`btn-edit-document-${personType}-${index}`}  // ✅ ID único
                                                    onClick={() => openModal(document)}
                                                    className="text-blue-600 hover:text-blue-800"
                                                    title="Editar"
                                                >
                                                    <TbEdit size={16} />
                                                </button>
                                                <button
                                                    id={`btn-remove-document-${personType}-${index}`}  // ✅ ID único
                                                    onClick={() => confirmDelete(document.id!)}
                                                    className="text-red-600 hover:text-red-800"
                                                    title="Excluir"
                                                >
                                                    <TbTrash size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modal de Cadastro/Edição */}
            {isModalOpen.value && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                        <h3 className="text-lg font-semibold mb-4">
                            {editingDocument ? 'Editar Documento' : 'Novo Documento'}
                        </h3>

                        <div className="space-y-4">
                            <SelectTextField
                                id={`document-type-${personType}`}  // ✅ ID único
                                label="Tipo de Documento *"
                                value={form.type}
                                onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
                                items={documentTypes}
                            />

                            <TextEdit
                                id={`document-number-${personType}`}  // ✅ ID único
                                label="Número do Documento *"
                                value={form.number}
                                onChange={(e) => setForm(prev => ({ ...prev, number: e.target.value }))}
                                placeholder="Digite o número do documento"
                            />

                            <TextEdit
                                id={`document-issuing-authority-${personType}`}  // ✅ ID único
                                label="Órgão Emissor"
                                value={form.issuingAuthority || ''}
                                onChange={(e) => setForm(prev => ({ ...prev, issuingAuthority: e.target.value }))}
                                placeholder="Ex: SSP/SP, DETRAN/SP"
                            />

                            <div className="grid grid-cols-2 gap-4">
                                <TextEdit
                                    id={`document-expedition-date-${personType}`}  // ✅ ID único
                                    label="Data de Expedição"
                                    type="date"
                                    value={form.expeditionDate || ''}
                                    onChange={(e) => setForm(prev => ({ ...prev, expeditionDate: e.target.value }))}
                                />

                                <TextEdit
                                    id={`document-expiration-date-${personType}`}  // ✅ ID único
                                    label="Data de Validade"
                                    type="date"
                                    value={form.expirationDate || ''}
                                    onChange={(e) => setForm(prev => ({ ...prev, expirationDate: e.target.value }))}
                                />
                            </div>

                            <TextEdit
                                id={`document-province-${personType}`}  // ✅ ID único
                                label="UF"
                                value={form.province || ''}
                                onChange={(e) => setForm(prev => ({ ...prev, province: e.target.value }))}
                                placeholder="Ex: SP, RJ, MG"
                                maxLength={2}
                            />
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                id={`btn-cancel-document-${personType}`}  // ✅ ID único
                                onClick={closeModal}
                                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                id={`btn-save-document-${personType}`}  // ✅ ID único
                                onClick={handleSubmit}
                                disabled={isLoading.value}
                                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                            >
                                {isLoading.value ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal de Confirmação de Exclusão */}
            <ConfirmationModal
                isOpen={isDeleteModalOpen.value}
                onCancel={() => {
                    isDeleteModalOpen.value = false;
                    setDocumentToDelete(null);
                }}
                onConfirm={handleDelete}
                title="Excluir Documento"
                id={documentToDelete || ''}
                text="Tem certeza que deseja excluir este documento? Esta ação não pode ser desfeita."
            />
        </div>
    );
};

export default PersonFormDocuments;
